import {MapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import {ILayerView, LayerInfo, LayerUpdateMetadata} from "@esWebAPI/es/model/Layer";
import { ColorThematic, SymbolThematic } from "@esWebAPI/es/model/Thematic";
import { ExpressionFilterSpecification } from "@maplibre/maplibre-gl-style-spec";
import {Map, source} from "azure-maps-control";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import {IEventChannel} from "@esWebAPI/es/model/Models";
import {AzurePolygonLayer} from "@esWebAPI/es/map/layer/azure/AzurePolygonLayer";
import {AzureLabelLayer} from "@esWebAPI/es/map/layer/azure/AzureLabelLayer";
import {AzureImageSpriteManager} from "@esWebAPI/es/map/layer/AzureImageSpriteManager";
import {AzureLineLayer} from "@esWebAPI/es/map/layer/azure/AzureLineLayer";
import _ from "lodash";


export class PolygonLayer extends MapLayer {

    private readonly _polygonLayer: AzurePolygonLayer;
    private readonly _lineLayer: AzureLineLayer;
    private readonly _labelLayer: AzureLabelLayer;

    private readonly stylerFactory: ThematicStylerFactory;
    private readonly spriteManager: AzureImageSpriteManager;

    private datasource: source.Source|undefined;
    private _colorThematic: ColorThematic|undefined;

    private debouncedOnUpdateThematic = _.debounce(this.onUpdateThematic.bind(this), 500);

    constructor(map: Map, layerView: ILayerView, layerInfo: LayerInfo,
                datasourceManager: DatasourceManager,
                spriteManager: AzureImageSpriteManager,
                stylerFactory: ThematicStylerFactory,
                eventChannel: IEventChannel,
                datasource? : source.Source) {

        super(map, layerView, layerInfo, datasourceManager, eventChannel);

        this.spriteManager = spriteManager;
        this.stylerFactory = stylerFactory;

        const colorStyler = stylerFactory.getColorStyler(layerView);

        this._polygonLayer = new AzurePolygonLayer(map, layerView, datasourceManager, colorStyler);
        this._lineLayer = new AzureLineLayer(map, layerView, datasourceManager, colorStyler);
        this._labelLayer = new AzureLabelLayer(map, layerView, layerInfo, datasourceManager, this.spriteManager, stylerFactory);

        this.datasource = datasource;

        es.api.getEventChannel().addListener('onUpdateThematic', this.debouncedOnUpdateThematic);

        this.createLayers().then(() => {
            this._onReady.resolve();
        });
    }

    private async createLayers() {

        this._colorThematic = await this.stylerFactory.getColorStyler(this.layerView).getThematic(this.layerView);

        if (!this.datasource) {
            this.datasource = await this.datasourceManager.create(this.layerView, this.layerInfo, false, this.getDatasourceFilter(this._colorThematic));
        }

        await this._polygonLayer.create(this.datasource, this._colorThematic);
        await this._lineLayer.create(this.datasource, this._colorThematic);

        if (this.layerView.styleSettings.defaultStyle.$type !== 'SymbolAndPolygonStyle') {
            // Labels are ignored when PolygonLayer is part of a PointAndPolygonLayer
            await this._labelLayer.create(this.datasource, this._colorThematic);
        }
    }

    private async onUpdateThematic(thematic: any): Promise<void> {
        if (thematic.layerViewId !== this.layerView.id) {
            return Promise.resolve();
        }
        this.update({isThematicChanged: true});
    }

    protected getColorThematicFilter(colorThematic?: ColorThematic): ExpressionFilterSpecification | undefined {
        return this.stylerFactory.getColorStyler(this.layerView).getFilterExpression(this.layerView, colorThematic);
    }

    protected getSymbolThematicFilter(_symbolThematic?: SymbolThematic): ExpressionFilterSpecification | undefined {
        return undefined;
    }

    protected hideFeature(featureId: number): void {
        this._lineLayer.hideFeature(featureId);
        this._polygonLayer.hideFeature(featureId);
        this._labelLayer.hideFeature(featureId);
    }

    protected showFeatures(): void {
        this._lineLayer.showFeatures();
        this._polygonLayer.showFeatures();
        this._labelLayer.showFeatures();
    }

    public  async update(layerUpdateMetadata: LayerUpdateMetadata): Promise<void> {

        if (!this.datasource) {
            return;
        }

        if (layerUpdateMetadata?.isMissingAttributes ||
            layerUpdateMetadata?.isCriteriaChanged ||
            layerUpdateMetadata?.isThematicChanged ||
            layerUpdateMetadata?.isFeatureInfoLastModifiedChanged) {

            this._colorThematic = await this.stylerFactory.getColorStyler(this.layerView).getThematic(this.layerView);

            const datasourceFilter = this.getDatasourceFilter(this._colorThematic);

            if (this.datasourceManager.isGeoJson(this.datasource.getId())) {
                this._polygonLayer.remove();
                this._lineLayer.remove();
                this._labelLayer.remove();
                this.datasource = await this.datasourceManager.recreate(this.datasource.getId(), this.layerView, this.layerInfo, datasourceFilter);
                await this._polygonLayer.recreate(this.layerView, this.datasource, this._colorThematic);
                await this._lineLayer.recreate(this.layerView,this.datasource, this._colorThematic);

                if (this.layerView.styleSettings.defaultStyle.$type !== 'SymbolAndPolygonStyle') {
                    await this._labelLayer.recreate(this.layerView, this.layerInfo, this.datasource, this._colorThematic);
                }

                this.eventChannel.fireEvent("onUpdateLayerComplete", this);
                return;
            } else { // isPbf
                await this.datasourceManager.recreate(this.datasource.getId(), this.layerView, this.layerInfo, datasourceFilter);
            }
        }

        await this._polygonLayer.update(this.layerView);
        await this._lineLayer.update(this.layerView);
        await this._labelLayer.update(this.layerView, this.layerInfo);
        this.eventChannel.fireEvent("onUpdateLayerComplete", this);
    }

    public destroy(): void {
        super.destroy();
        this._labelLayer.remove();
        this._polygonLayer.remove();
        this._lineLayer.remove();
        if (this.datasource) {
            this.datasourceManager.remove(this.datasource.getId());
        }
        es.api.getEventChannel().removeListener('onUpdateThematic', this.debouncedOnUpdateThematic);
    }

    public getLayerIds(): string[] {
        return [this._polygonLayer.getLayerId(), this._lineLayer.getLayerId(), this._labelLayer.getLayerId()];
    }

    public hide() {
        this._polygonLayer.hide();
        this._lineLayer.hide();
        this._labelLayer.hide();
    }

    public show() {

    }

    public get polygonLayer(): AzurePolygonLayer {
        return this._polygonLayer;
    }

    public get lineLayer(): AzureLineLayer {
        return this._lineLayer;
    }

    public get labelLayer(): AzureLabelLayer {
        return this._labelLayer;
    }

    public get colorThematic(): ColorThematic | undefined {
        return this._colorThematic;
    }

    public getActiveAzureLayerId(): string {
        return this._polygonLayer.getLayerId();
    }
}
